import { Inject, Injectable } from '@angular/core';
import { UserService } from '../user.service';
import { CredentialsService } from '../credentials.service';
import { User } from '../../classes/user';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { ApiService } from '../api.service';
import { APP_CONFIG } from '../../app-config.constants';
import { IAppConfig } from '../../app-config.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserPreferences } from '../../classes/user-preferences';
import { environment } from '../../../environments/environment-wrapper';
import { LoginOrRegister } from '../../components/login/sign-in/sign-in.component';
import { SocialType } from '../../classes/enums';
import { RECAPTCHA_HEADER } from '../../classes/leads';
import { ObjectResponse } from '../../classes/object-response';
import { SavedSearch } from '../../classes/saved-search';

@Injectable()
export class BrowserUserService extends UserService {
  private _localCacheKey: string = 'dotca_user';
  private _currentUser = new BehaviorSubject<User>(null);

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
    private http: HttpClient,
    private apiService: ApiService,
    private credentialsService: CredentialsService) {
    super();

    // We get a notification that someone just logged in or out, update the currentUser as appropriate.
    this.addSub(this.credentialsService.isLoggedIn.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.getUserFromDb().then(response => {
          const user = new User(JSON.parse(JSON.parse(response).result));
          // Plug in the email address from their access token.
          user.email = this.credentialsService.getEmailFromAccessToken();
          this._currentUser.next(user);
        });
      } else {
        // Otherwise clear their user and load from local.
        this.getUserFromLocal();
      }
    }));
    this.addStorageEventListener();
  }

  public checkIfAlreadyLoggedIn() {
    // The credentialService will either find a valid access token and kick off isLoggedIn = true, or it won't and we will
    // try to get a new one with the refresh token. It that is successful, the credentialsService will again kick of isLoggedIn = true.
    if (!this.credentialsService.isAccessTokenValid()) {
      this.apiService.getRefreshAccessToken().toPromise();
    }
  }

  public saveUser(user: User) {
    if (user) {
      return this.saveUserToStorage(user, true);
    }
  }

  public saveUserPreferences(preferences: UserPreferences) {
    const user = this._currentUser.getValue();
    user.preferences = preferences;
    this.saveUserToStorage(user, false);
  }

  public async registerUser(registration: LoginOrRegister, recaptchaKey: string) {
    const headers = new HttpHeaders({ [RECAPTCHA_HEADER]: recaptchaKey });
    return await this.http.post(environment.baseServerUrl + this.appConfig.urls.user.base, registration, { headers: headers })
      .toPromise();
  }

  public async checkUserAccounts(email: string): Promise<SocialType[]> {
    const params = new HttpParams()
      .set('email', email);
    return await this.http.get<ObjectResponse<SocialType[]>>(environment.baseServerUrl + this.appConfig.urls.user.accounts, { params: params })
      .pipe(map(res => res.result)).toPromise();
  }

  public removeLocalUser() {
    window.localStorage.removeItem(this._localCacheKey);
    this._currentUser.next(new User());
  }


  public isGoogleBot() {
    return navigator.userAgent.indexOf('Googlebot') >= 0;
  }

  public getLocalSavedSearches() {
    const storedUserString = window.localStorage.getItem(this._localCacheKey);
    if (storedUserString !== null) {
      const storedUser = this.getStoredUser(storedUserString);
     return storedUser.preferences.savedSearches;
    } else {
     return null;
    }
  }

  get currentUser() {
    {
      if (!this._currentUser.value) {
        this.getUserFromLocal();
      }
      return this._currentUser;
    }
  }

  /**
   * Calls the backend to get the user and all associated data for the currently logged in user.
   */
  private getUserFromDb() {
    return this.apiService.getRequest<string>(this.appConfig.urls.user.base)
      .build()
      .pipe(map(result => {
        if (result.error) { }
        return result.response;
      })).toPromise();
  }

  private getUserFromLocal() {
    const storedUserString = window.localStorage.getItem(this._localCacheKey);
    if (storedUserString !== null) {
      const storedUser = this.getStoredUser(storedUserString);
      this._currentUser.next(new User(storedUser));
    } else {
      this._currentUser.next(new User());
    }
  }

  private getStoredUser(storedUserString) {
    let storedUser = new User(JSON.parse(storedUserString));
    // Check if the version of data is old and we need to blow it all away and start fresh.
    if (storedUser.preferences.storedVersionNumber !== UserPreferences.latestVersionNumber) {
      window.localStorage.clear();
      storedUser = new User();
    }
    return storedUser;
  }

  private async saveUserToStorage(user: User, notifyOfChange: boolean) {
    // TODO: add in a fallback for cookie based saving if safari private
    window.localStorage.setItem(this._localCacheKey, JSON.stringify(user));
    if (user.email) {
      await this.apiService.postRequest<string>(this.appConfig.urls.user.save, user)
        .disableAutomaticRedirectToLogin()
        .build()
        .pipe(map(result => {
          if (result.response) {
            window.localStorage.setItem(this._localCacheKey, result.response);
            let updatedUser = new User(JSON.parse(result.response));
            user.updatedByStorage = true;
            user.preferences.savedSearches = updatedUser.preferences.savedSearches;
            // this._currentUser..next(user);
          }
          return !!result.response;
        })).toPromise();
    }
    if (notifyOfChange) {
      this._currentUser.next(user);
    }
  }

  private addStorageEventListener(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
  }

  private storageEventListener(event: StorageEvent) {
    if (event.storageArea === localStorage && !!event.newValue) {
        const user = new User(JSON.parse(event.newValue));
        const currentUser = this._currentUser.getValue();
        currentUser.updatedByStorage = true;
        currentUser.preferences.savedSearches = user.preferences.savedSearches;
        this._currentUser.next(currentUser);
    }
  }

  public restoreSession(sessionCacheKey: string) {
    return this.apiService.restoreAccessToken(sessionCacheKey);
  }

  public getSavedSearch(encryptedKey: string): Observable<SavedSearch> {
    const params = new HttpParams().set('key', encryptedKey);
    return this.http.get<ObjectResponse<SavedSearch>>(
      environment.baseServerUrl + this.appConfig.urls.user.retrieveSavedSearch,
      { params }
    ).pipe(map(res => res.result));
  }

  public unsubscribeSavedSearchNotifications(key: string): Observable<void> {
    const params = new HttpParams().set('key', key);
    return this.http.post<void>(
      environment.baseServerUrl + this.appConfig.urls.user.unsubscribeSavedSearchNotification,
      null, { params }
    ).pipe(map(() => {}));
  }
}
